import React, { PropsWithChildren, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { BaseModalType, useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2, Caption1 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { Modal } from '@ui/components/Modal'
import { CalendarIcon } from '@ui/icons/Calendar.icon'

import type { DxTradeExecutableSide } from '@model/DxTrade.types'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { getStepDecimals } from '../../utils/helpers'
import { LivePriceCell } from '../components/LivePriceCell'
import { TPSLButton } from '../components/TPSLButton'
import { useDxtradePendingOrders } from '../hooks/useDxtradePendingOrders'
import type { UseFixTradingWebSocketResult } from '../hooks/useFixTradingWebSocket'
import type { PendingOrderWithLots } from '../hooks/usePendingOrdersWithLots'
import {
  buildTradeWidgetUrl,
  formatDateTime,
  formatSymbol,
  getOpenLegTargetPrice,
  renderSide,
  upperCaseFirstLetter,
} from '../utils/formatters'
import { getCancelOrderParams } from '../utils/orderHelpers'
import { CancelOrderModal } from './CancelOrderModal'
import { TPSLModal } from './TPSLModal'
import { ValidTillModal } from './ValidTillModal'

type TradeDetailsPendingModalProps = {
  order: PendingOrderWithLots
  accountCurrency?: CurrencyType
  accountCode?: string
  cancelOrder?: UseFixTradingWebSocketResult['cancelOrder']
  newOrder?: UseFixTradingWebSocketResult['newOrder']
  changePendingOrderSlTp?: UseFixTradingWebSocketResult['changePendingOrderSlTp']
  replaceOrder?: UseFixTradingWebSocketResult['replaceOrder']
  instrumentMeta: { pipSize?: number | null; priceIncrement?: number | null }
}

export const TradeDetailsPendingModal: BaseModalType<TradeDetailsPendingModalProps> = (props) => {
  const {
    close,
    order,
    accountCurrency,
    accountCode,
    instrumentMeta,
    cancelOrder,
    newOrder,
    changePendingOrderSlTp,
    replaceOrder,
  } = props
  const { t } = useTranslation()
  const { formatNumber } = useFormatNumber()
  const navigate = useNavigate()
  const fractionDigits = getStepDecimals(instrumentMeta.priceIncrement)

  const handleNavigateToWidget = useCallback(() => {
    navigate(
      buildTradeWidgetUrl(
        order.instrument || '',
        (order.side ?? 'BUY') as DxTradeExecutableSide,
        'chart'
      )
    )
    close()
  }, [navigate, order.instrument, order.side, close])

  const cancelOrderModal = useModal(CancelOrderModal)
  const tpslModal = useModal(TPSLModal)
  const validTillModal = useModal(ValidTillModal)

  const { data: pendingOrders } = useDxtradePendingOrders(accountCode ?? '')

  const currentOrder = useMemo<PendingOrderWithLots>(() => {
    const found = pendingOrders?.find((o) => o.orderId === order.orderId)
    if (!found) return order
    return { ...order, ...found }
  }, [pendingOrders, order])

  return (
    <Modal>
      <Modal.Header canClose>{t('Portfolio.Order details')}</Modal.Header>
      <Modal.Body>
        <Box fullWidth>
          <Box
            flex
            direction='column'
            px='x16'
            py='x16'
            mb='x16'
            radius='x16'
            border='outline-low-em'
            onClick={handleNavigateToWidget}
          >
            <Box flex justify='space-between'>
              <Body1 color='text-high-em' medium>
                {formatSymbol(order.instrument, order.description)}
              </Body1>
              <Body1 color='text-high-em' medium>
                {(() => {
                  const p = getOpenLegTargetPrice(order)
                  return p != null ? (
                    <SensitiveData>
                      {formatNumber(p, {
                        minDecimalPlaces: fractionDigits,
                        maxDecimalPlaces: fractionDigits,
                      })}
                    </SensitiveData>
                  ) : (
                    '-'
                  )
                })()}
              </Body1>
            </Box>
            <Box flex justify='space-between'>
              <Label>
                <Caption1>
                  {renderSide(order.side, Caption1)} {upperCaseFirstLetter(order.type)}{' '}
                  <span>
                    <SensitiveData>{order.sizeLots || ''}</SensitiveData>
                  </span>
                </Caption1>
              </Label>

              <SensitiveData>
                <LivePriceCell
                  trade={{
                    symbol: order.instrument || '',
                    side: order.side,
                    priceIncrement: instrumentMeta.priceIncrement,
                  }}
                  priceType='order'
                  TypographyComp={Caption1}
                />
              </SensitiveData>
            </Box>
          </Box>

          <Box
            flex
            direction='column'
            px='x16'
            py='x16'
            mb='x16'
            radius='x16'
            border='outline-low-em'
          >
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.OrderID')}</Label>
              <Value>{order.orderId}</Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.PlacedOn')}</Label>
              <Value>{formatDateTime(order.issueTime)}</Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.Size')}</Label>
              <Value>
                <SensitiveData>{order.sizeLots || ''}</SensitiveData>
              </Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.CurrentPrice')}</Label>

              <LivePriceCell
                trade={{
                  symbol: order.instrument || '',
                  side: order.side,
                  priceIncrement: instrumentMeta.priceIncrement,
                }}
                priceType='order'
                TypographyComp={Body2}
              />
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.Target')}</Label>
              <Value>
                {(() => {
                  const p = getOpenLegTargetPrice(order)
                  return p != null ? (
                    <SensitiveData>
                      {formatNumber(p, {
                        minDecimalPlaces: fractionDigits,
                        maxDecimalPlaces: fractionDigits,
                      })}
                    </SensitiveData>
                  ) : (
                    '-'
                  )
                })()}
              </Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.StopLoss')}</Label>
              <Value>
                {currentOrder.stopLoss != null ? (
                  <SensitiveData>
                    {formatNumber(currentOrder.stopLoss, {
                      minDecimalPlaces: fractionDigits,
                      maxDecimalPlaces: fractionDigits,
                    })}
                  </SensitiveData>
                ) : (
                  '—'
                )}
              </Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.TakeProfit')}</Label>
              <Value>
                {currentOrder.takeProfit != null ? (
                  <SensitiveData>
                    {formatNumber(currentOrder.takeProfit, {
                      minDecimalPlaces: fractionDigits,
                      maxDecimalPlaces: fractionDigits,
                    })}
                  </SensitiveData>
                ) : (
                  '—'
                )}
              </Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.ValidTill')}</Label>
              <Value>
                <Box flex align='center' justify='center' gap='x4'>
                  <Box>{formatDateTime(currentOrder.expireDate ?? undefined)}</Box>
                  <IconButton.Ghost
                    size='sm'
                    icon={CalendarIcon}
                    onClick={() => {
                      validTillModal.open({
                        order: currentOrder,
                        accountCode,
                        replaceOrder,
                      })
                    }}
                  />
                </Box>
              </Value>
            </Box>
          </Box>

          <TPSLButton
            onPress={() =>
              tpslModal.open({
                order: currentOrder,
                accountCurrency,
                instrumentCurrency: (order.instrumentCurrency ?? undefined) as
                  | CurrencyType
                  | undefined,
                accountCode: accountCode,
                instrumentMeta: instrumentMeta,
                cancelOrder,
                newOrder,
                changePendingOrderSlTp,
                replaceOrder,
                onTPSLChange: close,
              })
            }
          />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Primary
          onClick={() => {
            close()
            cancelOrderModal.open({
              order: currentOrder,
              priceIncrement: instrumentMeta.priceIncrement,
              onConfirm: (onSettled) => {
                if (!cancelOrder) return false
                const params = getCancelOrderParams(currentOrder)
                if (!params) return false
                return Boolean(cancelOrder({ ...params, onSettled }))
              },
            })
          }}
        >
          {t('Portfolio.Cancel Order')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}

const Label: React.FC<PropsWithChildren> = ({ children }) => (
  <Body2 color='text-med-em'>{children}</Body2>
)

const Value: React.FC<PropsWithChildren> = ({ children }) => (
  <Body2 color='text-high-em'>{children}</Body2>
)
